import { useQuery, UseQueryResult } from "react-query";
import { useSupabase } from "#next/hooks/use-supbase";
import { useSession } from "@supabase/auth-helpers-react";
import { assert } from "#/utils/general";
import { supabaseThenResult } from "#/lib/supabase/supabase-then";
import { Logger } from "#/lib/logging";
import { TableRow } from "#/types/supabase";
import { useRealtimeSubscription } from "#next/hooks/with-react-context/use-realtime-subscription/use-realtime-subscription";


export const useUser = (): UseQueryResult<TableRow<"users">> => {
  const session = useSession();
  const supabase = useSupabase();

  const userId = session?.user?.id;
  // Safe query key that won't cause errors when user is not logged in
  const queryKey = userId ? [ "user", userId ] : [ "user", "anonymous" ];

  const queryResult = useQuery<TableRow<"users">>(queryKey, async () => {
    assert(!!userId, "User must be logged in to fetch user data");
    const {
      data, error
    } = await supabase.from("users")
      .select("*")
      .eq("id", userId)
      .single()
      .then(supabaseThenResult(new Error().stack));
    if (error) {
      Logger.error({
        error,
        message: "Error fetching user record"
      });
      throw error;
    }
    return data;
  }, {
    enabled: !!userId
  });

  // Only set up realtime subscription if user is logged in
  useRealtimeSubscription({
    table: "users",
    onUpdate: async ({ new: newUser }) => {
      if (!!userId && newUser.id === userId) {
        await queryResult.refetch();
      }
    },
    // Only enable subscription when user is logged in
    condition: !!userId
  });

  return queryResult;
};